import { useEffect, useState } from 'react';
import { Api } from '@infra/api';
import { Analyses, AnalysesFull, Material } from '@domain/models';
import { Modal } from '../components/Modal';
import { DataTable } from '../components/DataTable';
import { SearchSelect } from '../components/SearchSelect';
// @ts-ignore
import pdfMake from 'pdfmake/build/pdfmake';
// @ts-ignore
import pdfFonts from 'pdfmake/build/vfs_fonts';

const empty: Analyses = {
  analyses_name: '',
  analyses_description: '',
  material_id: null,
  analyses_status: 'active',
};

const genderLabel = (gender?: string | null) => gender === 'any' ? 'Любой' : gender || '—';
const groupLabel = (group?: AnalysesFull['norms'][number]['group']) =>
  group?.name ? `${group.name} (${genderLabel(group.gender)})` : '—';

export function AnalysesPage() {
  const [items, setItems] = useState<Analyses[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [form, setForm] = useState<Analyses>({ ...empty });
  const [saving, setSaving] = useState(false);
  const [editWithNorms, setEditWithNorms] = useState<AnalysesFull | null>(null);

  useEffect(() => {
    Promise.all([
      Api.listAnalyses(),
      Api.listMaterials()
    ]).then(([analyses, materials]) => {
      setItems(analyses);
      setMaterials(materials);
      if (materials[0]) {
        setForm(prev => ({ ...prev, material_id: materials[0].id }));
      }
    }).catch(console.error);
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const created = await Api.createAnalyses(form);
      setItems([created, ...items]);
      setForm({ ...empty });
    } finally {
      setSaving(false);
    }
  }

  async function exportToPDF(analysis: AnalysesFull) {
    try {
      if (pdfMake && pdfFonts) {
        const vfs = (pdfFonts as any).pdfMake?.vfs || (pdfFonts as any).default?.pdfMake?.vfs || (pdfFonts as any).vfs || {};
        if (vfs && Object.keys(vfs).length > 0) {
          (pdfMake as any).vfs = vfs;
        }
      }

      let logoDataUrl = '';
      let logoWidth = 80;
      let logoHeight = 80;
      try {
        const response = await fetch('/logo.png');
        const blob = await response.blob();
        logoDataUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
        
        const img = new Image();
        img.src = logoDataUrl;
        await new Promise((resolve) => {
          img.onload = () => {
            const aspectRatio = img.width / img.height;
            if (aspectRatio > 1) {
              logoWidth = 80;
              logoHeight = 80 / aspectRatio;
            } else {
              logoHeight = 80;
              logoWidth = 80 * aspectRatio;
            }
            resolve(null);
          };
          img.onerror = resolve;
        });
      } catch (e) {
        console.warn('Не удалось загрузить логотип:', e);
      }

      const tableBody = analysis.norms.map(norm => {
        const range = norm.norm_type === 'numeric' 
          ? `${norm.numeric_min ?? ''}–${norm.numeric_max ?? ''}`
          : '—';
        
        const serious = norm.norm_type === 'numeric'
          ? `${norm.serious_lte != null ? `≤${norm.serious_lte}` : ''} ${norm.serious_gte != null ? `≥${norm.serious_gte}` : ''}`.trim() || '—'
          : '—';

        const unit = norm.unit?.unit_name ?? norm.norm_unit ?? '—';
        const group = groupLabel(norm.group);
        const context = norm.context?.context_name ?? '—';
        
        const synonyms = norm.norm_type === 'text' && norm.text_rules && norm.text_rules.length > 0
          ? norm.text_rules.map(r => `${r.value_text}→${r.normalized_code}${r.is_allowed ? '✓' : '✗'}`).join(', ')
          : norm.norm_type === 'text' ? 'Нет синонимов' : '—';

        return [
          norm.norm_name || '—',
          norm.norm_code || '—',
          norm.norm_type,
          range,
          serious,
          unit,
          group,
          context,
          synonyms
        ];
      });

      const materialName = materials.find(m => m.id === analysis.material_id)?.material_name || '—';

      const docDefinition: any = {
        pageSize: 'A4',
        pageMargins: [40, 60, 40, 60],
        header: function(currentPage: number, pageCount: number) {
          return {
            columns: [
              logoDataUrl ? {
                image: logoDataUrl,
                width: logoWidth,
                height: logoHeight,
                margin: [0, 0, 10, 0]
              } : { text: '', width: 0 },
            ],
            margin: [40, 20, 40, 10]
          };
        },
        footer: function(currentPage: number, pageCount: number) {
          return {
            text: `Страница ${currentPage} из ${pageCount}`,
            alignment: 'right',
            margin: [40, 10, 40, 20],
            fontSize: 10
          };
        },
        content: [
          { text: 'Анализ', fontSize: 16, bold: true, margin: [0, 0, 0, 10] },
          { text: `Название: ${analysis.analyses_name}`, fontSize: 12, margin: [0, 0, 0, 5] },
          ...(analysis.analyses_description ? [{ text: `Описание: ${analysis.analyses_description}`, fontSize: 12, margin: [0, 0, 0, 5] }] : []),
          { text: `Исследуемый материал: ${materialName}`, fontSize: 12, margin: [0, 0, 0, 5] },
          { text: `Статус: ${analysis.analyses_status}`, fontSize: 12, margin: [0, 0, 0, 15] },
          ...(analysis.norms.length > 0 ? [
            { text: `Параметры (${analysis.norms.length})`, fontSize: 14, bold: true, margin: [0, 0, 0, 10] },
            {
              table: {
                headerRows: 1,
                widths: ['*', '*', 'auto', '*', '*', 'auto', '*', '*', '*'],
                body: [
                  [
                    { text: 'Параметр', bold: true, fillColor: '#ef2a6b', color: '#ffffff' },
                    { text: 'Код', bold: true, fillColor: '#ef2a6b', color: '#ffffff' },
                    { text: 'Тип', bold: true, fillColor: '#ef2a6b', color: '#ffffff' },
                    { text: 'Диапазон', bold: true, fillColor: '#ef2a6b', color: '#ffffff' },
                    { text: 'Серьёзные', bold: true, fillColor: '#ef2a6b', color: '#ffffff' },
                    { text: 'Ед.', bold: true, fillColor: '#ef2a6b', color: '#ffffff' },
                    { text: 'Группа', bold: true, fillColor: '#ef2a6b', color: '#ffffff' },
                    { text: 'Контекст', bold: true, fillColor: '#ef2a6b', color: '#ffffff' },
                    { text: 'Синонимы', bold: true, fillColor: '#ef2a6b', color: '#ffffff' }
                  ],
                  ...tableBody.map((row, index) => 
                    row.map(cell => ({ 
                      text: String(cell), 
                      fontSize: 8,
                      fillColor: index % 2 === 0 ? '#f5f7fb' : '#ffffff'
                    }))
                  )
                ]
              },
              layout: 'lightHorizontalLines'
            }
          ] : [
            { text: 'Нормы для этого анализа не найдены', fontSize: 12, italics: true, margin: [0, 10, 0, 0] }
          ])
        ],
        defaultStyle: {
          fontSize: 10
        }
      };

      const fileName = `Анализ_${analysis.analyses_name.replace(/[^a-zA-Zа-яА-Я0-9]/g, '_')}.pdf`;
      if (!pdfMake) {
        throw new Error('pdfMake не загружен');
      }
      if (!(pdfMake as any).createPdf) {
        throw new Error('pdfMake.createPdf не найден');
      }
      
      try {
        (pdfMake as any).createPdf(docDefinition).download(fileName);
      } catch (pdfError) {
        console.error('Ошибка при создании PDF:', pdfError);
        throw pdfError;
      }
    } catch (error: any) {
      console.error('Ошибка при экспорте в PDF:', error);
      console.error('Детали ошибки:', {
        message: error?.message,
        stack: error?.stack,
        pdfMake: !!pdfMake,
        pdfFonts: !!pdfFonts
      });
      alert(`Не удалось экспортировать анализ в PDF: ${error?.message || 'Неизвестная ошибка'}`);
    }
  }

  return (
    <div>
      <div className="toolbar">
        <div className="title"><h2 style={{margin:0}}>Анализы</h2></div>
      </div>
      <div className="row">
        <form className="card" onSubmit={submit} style={{maxWidth:560}}>
          <h3 style={{marginTop:0}}>Новый анализ</h3>
          <label>Название</label>
          <input value={form.analyses_name} onChange={e=>setForm({...form, analyses_name:e.target.value})} required />
          <label>Описание</label>
          <textarea value={form.analyses_description ?? ''} onChange={e=>setForm({...form, analyses_description:e.target.value})} />
          <label>Исследуемый материал</label>
          <SearchSelect
            options={materials.map(m => ({ value: String(m.id), label: m.material_name }))}
            value={form.material_id ? String(form.material_id) : ''}
            onChange={(v) => setForm({...form, material_id: v ? Number(v) : null})}
          />
          <label>Статус</label>
          <select value={form.analyses_status} onChange={e=>setForm({...form, analyses_status:e.target.value})}>
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </select>
          <div style={{marginTop:12}}>
            <button disabled={saving}>{saving? 'Сохранение…':'Создать'}</button>
          </div>
        </form>

        <div style={{flex:2}}>
          <DataTable
            data={items}
            title="Список"
            searchFields={['analyses_name', 'analyses_description']}
            searchPlaceholder="Поиск по названию, описанию..."
            onRowClick={async (item) => {
              if (item.id) {
                const full = await Api.getAnalysesFullById(item.id);
              if (full) {
                setEditWithNorms(full);
                }
              }
            }}
            columns={[
              { key: 'analyses_name', title: 'Название' },
              { 
                key: 'material_id', 
                title: 'Исследуемый материал',
                render: (item: Analyses) => materials.find(m => m.id === item.material_id)?.material_name || '—'
              },
              { key: 'analyses_status', title: 'Статус' }
            ]}
          />
        </div>
      </div>
      {editWithNorms && (
        <Modal title={`Анализ: ${editWithNorms.analyses_name}`} onClose={()=>setEditWithNorms(null)}>
          {/* Форма редактирования */}
          <div className="row">
            <div>
              <label>Название</label>
              <input 
                value={editWithNorms.analyses_name} 
                onChange={e=>setEditWithNorms({...editWithNorms!, analyses_name:e.target.value})} 
                required 
              />
            </div>
            <div>
              <label>Описание</label>
              <textarea 
                value={editWithNorms.analyses_description} 
                onChange={e=>setEditWithNorms({...editWithNorms!, analyses_description:e.target.value})}
              />
            </div>
          </div>
          <div className="row">
            <div>
              <label>Исследуемый материал</label>
              <SearchSelect
                options={materials.map(m => ({ value: String(m.id), label: m.material_name }))}
                value={editWithNorms.material_id ? String(editWithNorms.material_id) : ''}
                onChange={(v) => setEditWithNorms({...editWithNorms!, material_id: v ? Number(v) : null})}
              />
            </div>
            <div>
              <label>Статус</label>
              <select 
                value={editWithNorms.analyses_status} 
                onChange={e=>setEditWithNorms({...editWithNorms!, analyses_status:e.target.value})}
              >
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </div>
          </div>
          
          {/* Кнопки управления */}
          <div style={{marginTop:12, display:'flex', gap:8, alignItems:'center'}}>
            <div style={{display:'flex', gap:8}}>
              <button onClick={async ()=>{ 
                const analysisToUpdate = {
                  id: items.find(a => a.analyses_name === editWithNorms.analyses_name)?.id,
                  analyses_name: editWithNorms.analyses_name,
                  analyses_description: editWithNorms.analyses_description,
                  material_id: editWithNorms.material_id,
                  analyses_status: editWithNorms.analyses_status
                };
                if (analysisToUpdate.id != null) { 
                  const updated = await Api.updateAnalyses(analysisToUpdate.id, analysisToUpdate); 
                  setItems(items.map(x=> x.id === updated.id ? updated : x)); 
                  setEditWithNorms(null); 
                }
              }}>
                Сохранить
              </button>
              <button className="secondary" onClick={()=>setEditWithNorms(null)}>Отмена</button>
            </div>
            <button 
              style={{background: '#10b981', marginLeft: 'auto'}} 
              onClick={() => exportToPDF(editWithNorms)}
            >
              📄 Экспорт в PDF
            </button>
          </div>

          {/* Разделитель */}
          <hr style={{margin: '20px 0', border: 'none', borderTop: '1px solid var(--input-border)'}} />

          {/* Связанные нормы */}
          <h4>Связанные нормы ({editWithNorms.norms.length})</h4>
          {editWithNorms.norms.length > 0 ? (
            <table className="table">
              <thead>
                <tr>
                  <th>Параметр</th>
                  <th>Код</th>
                  <th>Тип</th>
                  <th>Диапазон</th>
                  <th>Серьёзные</th>
                  <th>Ед.</th>
                  <th>Группа</th>
                  <th>Контекст</th>
                  <th>Синонимы</th>
                </tr>
              </thead>
              <tbody>
                {editWithNorms.norms.map(n => (
                  <tr key={n.id}>
                    <td>{n.norm_name || '—'}</td>
                    <td>{n.norm_code || '—'}</td>
                    <td>{n.norm_type}</td>
                    <td>{n.norm_type === 'numeric' ? `${n.numeric_min ?? ''}–${n.numeric_max ?? ''}` : '—'}</td>
                    <td>{n.norm_type === 'numeric' ? `${n.serious_lte!=null? `≤${n.serious_lte}`:''} ${n.serious_gte!=null? `≥${n.serious_gte}`:''}`.trim() || '—' : '—'}</td>
                    <td>{n.unit?.unit_name ?? n.norm_unit ?? '—'}</td>
                    <td>{groupLabel(n.group)}</td>
                    <td>{n.context?.context_name ?? '—'}</td>
                    <td>
                      {n.norm_type === 'text' && n.text_rules && n.text_rules.length > 0 ? (
                        <div style={{maxWidth: 300}}>
                          {n.text_rules.map((rule, idx) => (
                            <div key={idx} style={{marginBottom: 4, fontSize: '0.9em', padding: '4px 8px', background: 'var(--input-bg)', borderRadius: 4}}>
                              <strong>{rule.value_text}</strong> → {rule.normalized_code} {rule.is_allowed ? '✓' : '✗'}
                            </div>
                          ))}
                        </div>
                      ) : n.norm_type === 'text' ? (
                        <span style={{color: 'var(--muted)', fontStyle: 'italic'}}>Нет синонимов</span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{color: 'var(--muted)', fontStyle: 'italic'}}>Нормы для этого анализа не найдены</p>
          )}
        </Modal>
      )}
    </div>
  );
}


